import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Item } from '../types/Types'; // Adjust path to your Item type
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';

type NewItem = Omit<
  Item,
  '_id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | '__v'
>;



const useItems = (initialAppendResults = false, onlyActive: boolean = false, skipInitialFetch: boolean = false) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [appendResults, setAppendResults] = useState<boolean>(false);

  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const baseUrl = onlyActive ? `${apiUrl}/api/items/active` : `${apiUrl}/api/items`;

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        items: Item[];
        totalCount: number;
        page: number;
        totalPages: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          search: searchQuery,
          sort: sortBy,
          order: sortOrder,
          page,
          limit: rowsPerPage,
        },
      });

      setItems(prev =>
        appendResults && page !== 1 ? [...prev, ...response.data.items] : response.data.items
      );

      setTotalCount(response.data.totalCount);
      setPage(response.data.page);
      setTotalPages(response.data.totalPages);

    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch items';
      const messageKey = err.response?.data?.messageKey;
      setError(message);
      setErrorMessageKey(messageKey || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip initial fetch when only utility functions are needed (e.g., DocumentItemsTable)
    if (skipInitialFetch) return;
    fetchItems();
  }, [searchQuery, sortBy, sortOrder, page, rowsPerPage, skipInitialFetch]);

  const getItemById = async (id: string): Promise<Item | null> => {
  setError(null);
  setErrorMessageKey(null);
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');

    const res = await axios.get<Item>(`${baseUrl}/${id}`, {
      headers: { 'x-auth-token': token },
    });

    return res.data;
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || 'Failed to fetch item';
    const messageKey = err.response?.data?.messageKey;
    setError(message);
    setErrorMessageKey(messageKey || null);
    return null;
  }
};


  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };


  const createItem = async (data: NewItem) => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      await axios.post(baseUrl, data, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      showToast(t('toast.created', { item: t('sidebar.items') }), 'success');
      await fetchItems(); // re-fetch items after creating
      setPage(1); // optional: go back to page 1

    } catch (err: any) {
      const message = err.response?.data?.error || err.message || "Failed to create item";
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
    } finally {
      setLoading(false);
    }
  };


    const updateItem = async (id: string, data: Partial<NewItem>) => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const response = await axios.put(`${baseUrl}/${id}`, data, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      window.dispatchEvent(new CustomEvent("item-updated", { detail: response.data.item }));

      showToast(t('toast.updated', { item: t('sidebar.items') }), 'success');
      await fetchItems(); // re-fetch list
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || "Failed to update item";
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
    } finally {
      setLoading(false);
    }
  };


  const deleteItem = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      await axios.delete(`${baseUrl}/${id}`, {
        headers: { 'x-auth-token': token },
      });

      showToast(t('toast.deleted', { item: t('sidebar.items') }), 'success');
      setItems((prev) => prev.filter((item) => item._id !== id));
      await fetchItems();

      return true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete item';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const bulkUploadItems = async (rows: any[]) => {
    try {
      // Convert parsed data to Excel file and upload using the new system
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Items');

      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const file = new File([buffer], 'items_upload.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const { uploadItemsExcel } = await import('../utils/itemsExcel');
      const result = await uploadItemsExcel(file);
      await fetchItems(); // Refresh the items list

      // Show success notification if no errors
      if (result?.results?.errors?.length === 0) {
        showToast(t('toasts.uploadSuccess'), 'success');
      }

    } catch (error: any) {
      showToast(`Excel upload failed: ${error.message}`, 'error');
    }
  };

  const downloadItemsExcel = async () => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const { downloadItemsExcel: downloadExcel } = await import('../utils/itemsExcel');
      const lang = (localStorage.getItem('i18nextLng') || 'en').split('-')[0];

      await downloadExcel(lang, undefined, {
        search: searchQuery,
      });
    } catch (err: any) {
      const message = err.message || 'Failed to download Excel';
      const messageKey = err.response?.data?.messageKey;
      setError(message);
      setErrorMessageKey(messageKey || null);
    } finally {
      setLoading(false);
    }
  };

  // Bulk fetch items by IDs - used for preloading items in tables
  const bulkFetchItemsByIds = useCallback(async (ids: string[]): Promise<Item[]> => {
    if (!ids || ids.length === 0) return [];

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.post<{ items: Item[] }>(
        `${apiUrl}/api/items/bulk-ids`,
        { ids },
        { headers: { 'x-auth-token': token } }
      );

      return response.data.items || [];
    } catch (err: any) {
      return [];
    }
  }, [apiUrl]);

  // Bulk lookup items by idents - used for Excel import (batched to handle large files)
  const bulkLookupItemsByIdents = useCallback(async (idents: string[]): Promise<Item[]> => {
    if (!idents || idents.length === 0) return [];

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const BATCH_SIZE = 500;
      const results: Item[] = [];
      for (let i = 0; i < idents.length; i += BATCH_SIZE) {
        const batch = idents.slice(i, i + BATCH_SIZE);
        const response = await axios.post<{ items: Item[] }>(
          `${apiUrl}/api/items/bulk-lookup`,
          { idents: batch },
          { headers: { 'x-auth-token': token } }
        );
        results.push(...(response.data.items || []));
      }
      return results;
    } catch (err: any) {
      return [];
    }
  }, [apiUrl]);

  const downloadItemsPDF = async () => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');
      const lang = (localStorage.getItem('i18nextLng') || 'en').split('-')[0];

      const response = await axios.get(`${apiUrl}/api/items/pdf`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob',
        params: { lang }
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `items-list-${lang}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to download PDF';
      const messageKey = err.response?.data?.messageKey;
      setError(message);
      setErrorMessageKey(messageKey || null);
    } finally {
      setLoading(false);
    }
  };

  return {
    items,
    loading,
    error,
    errorMessageKey,
    deleteItem,
    createItem,
    updateItem,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    page,
    setPage,
    totalPages,
    totalCount,
    rowsPerPage,
    onRowsPerPageChange,
    setRowsPerPage,
    appendResults,
    setAppendResults,
    fetchItems,
    getItemById,
    bulkUploadItems,
    downloadItemsExcel,
    downloadItemsPDF,
    bulkFetchItemsByIds,
    bulkLookupItemsByIdents
  };
};

export default useItems;
